import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { doRegistrationLkm } from 'feature/auth/redux/action/doRegistration'
import { RegistrationLkmData } from 'feature/registration/types'
import { MainLayout } from 'layouts'
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { Loading } from 'router/Loading'
import { useThunkDispatch } from 'store/hooks'
import { UserDataForm } from 'widget/UserDataForm/UserDataForm'

import { useRegistrationForm } from 'feature/registration/hooks/useRegistrationForm'
import {
  deleteRegistrationLkmFields,
  getRegistrationLkmFields,
  getRegistrationLkmStep,
  setRegistrationLkmFields,
} from 'feature/registration/storage/registrationLkmStorage'

import { useGetDealerProductTypesQuery } from 'feature/registration/api/registrationLkmApi'
import { LkmRegistrationStepTwo } from './components/LkmRegistrationStepTwo'
import {
  createRegistrationLkmDefaultValues,
  registrationLkmSchema,
} from './helpers/registrationLkmSchema'
import { NavigationPrompt } from 'widget/NavigationPrompt/NavigationPrompt'
import { CreateModalConfirm } from 'widget/CreateModalConfirm'

export function LkmRegistration() {
  const navigate = useNavigate()
  const thunkDispatch = useThunkDispatch()
  const [step, setStep] = useState(() => getRegistrationLkmStep())

  const {
    data: productTypes = [],
    isLoading: isLoadingProductTypes,
    isError: isProductTypesError,
  } = useGetDealerProductTypesQuery(undefined, { skip: step !== 2 })

  const defaultValues = createRegistrationLkmDefaultValues()

  const storageHandlers = useMemo(
    () => ({
      get: getRegistrationLkmFields,
      set: setRegistrationLkmFields,
      delete: deleteRegistrationLkmFields,
    }),
    [],
  )

  const onSuccessRedirect = useCallback(
    ({ finish }: { finish: boolean }) => {
      if (finish) {
        navigate('/orders')
      }
    },
    [navigate],
  )

  const { formMethods, isExitModal, handleSubmit, handleConfirm, authInfo } =
    useRegistrationForm<RegistrationLkmData>({
      schema: registrationLkmSchema,
      defaultValues,
      storageHandlers,
      onSuccessRedirect,
      validationContext: { step },
    })

  const onSubmit = () => {
    handleSubmit(values => {
      thunkDispatch(doRegistrationLkm(values))
    })
  }

  useEffect(() => {
    const currentFields = getRegistrationLkmFields()
    setRegistrationLkmFields(currentFields, step)
  }, [step])

  const handleNextStep = async () => {
    const fieldsToValidate: Array<keyof RegistrationLkmData> = [
      'lastname',
      'firstname',
      'middlename',
      'email',
      'personalAgree',
      'address',
    ]
    const isValid = await formMethods.trigger(fieldsToValidate, {
      shouldFocus: true,
    })
    if (isValid) setStep(2)
  }

  const handleBackToFirstStep = () => setStep(1)

  const handleFinalSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const isValid = await formMethods.trigger(
      ['productTypes', 'serviceRegions'],
      {
        shouldFocus: true,
      },
    )
    if (!isValid) return

    onSubmit()
  }

  if (authInfo.isLoading || isLoadingProductTypes)
    return (
      <MainLayout>
        <Loading />
      </MainLayout>
    )

  return (
    <MainLayout>
      <FormProvider {...formMethods}>
        <div className='container'>
          {step === 1 ? (
            <UserDataForm
              title='Личные данные'
              sendButton={{
                endIcon: <ChevronRightIcon />,
                text: 'Далее',
              }}
              onSubmit={handleNextStep}
              isFirstStep
              isLkm
              hasAdvCheckbox={false}
            />
          ) : (
            <LkmRegistrationStepTwo
              onBack={handleBackToFirstStep}
              onSubmit={handleFinalSubmit}
              isSubmitting={authInfo.isLoading}
              productTypes={productTypes}
              productTypesError={isProductTypesError}
            />
          )}
        </div>
      </FormProvider>
      <NavigationPrompt when={isExitModal} onConfirm={handleConfirm}>
        {CreateModalConfirm()}
      </NavigationPrompt>
    </MainLayout>
  )
}
